/**
 * Defensive parsing for data that crosses a trust boundary: save files (which
 * players can edit) and network messages (which peers can forge). Everything is
 * coerced into a well-formed shape or rejected.
 */
import { sanitizeFarmName, type GameAction } from './actions';
import { MAX_ORDERS } from './constants';
import { FLOWER_IDS, isFlowerId, type FlowerId } from './flowers';
import { isValidPlotId } from './grid';
import {
  createInitialState,
  STATE_VERSION,
  type AlmanacEntry,
  type FlowerStock,
  type GameState,
  type Order,
  type Plot,
} from './state';
import { isUpgradeId, maxLevel, UPGRADE_IDS } from './upgrades';

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);

function num(v: unknown, fallback: number, min = -Infinity, max = Infinity): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;
}

function int(v: unknown, fallback: number, min = -Infinity, max = Infinity): number {
  return Math.floor(num(v, fallback, min, max));
}

function flowerRecord<T>(
  raw: unknown,
  parse: (v: unknown) => T | null,
): Partial<Record<FlowerId, T>> {
  const out: Partial<Record<FlowerId, T>> = {};
  if (!isObj(raw)) return out;
  for (const id of FLOWER_IDS) {
    const value = parse(raw[id]);
    if (value !== null) out[id] = value;
  }
  return out;
}

function parsePlot(raw: unknown, id: number, fallback: Plot): Plot {
  if (!isObj(raw)) return fallback;
  const crop = isObj(raw.crop) && isFlowerId(raw.crop.species) ? raw.crop : null;
  return {
    id,
    unlocked: raw.unlocked === true,
    moisture: num(raw.moisture, 0, 0, 1),
    crop: crop
      ? {
          species: crop.species as FlowerId,
          growth: num(crop.growth, 0, 0, 1),
          golden: crop.golden === true,
        }
      : null,
  };
}

function parseOrder(raw: unknown): Order | null {
  if (!isObj(raw) || !Array.isArray(raw.items)) return null;
  const items = raw.items
    .filter(isObj)
    .filter((item) => isFlowerId(item.species))
    .map((item) => ({ species: item.species as FlowerId, qty: int(item.qty, 1, 1, 99) }))
    .slice(0, 3);
  if (items.length === 0) return null;
  return {
    id: int(raw.id, 0, 0),
    customer: int(raw.customer, 0, 0, 99),
    items,
    reward: int(raw.reward, 0, 0, 1e7),
    xp: int(raw.xp, 0, 0, 1e6),
  };
}

/**
 * Coerces untrusted JSON into a valid GameState, filling gaps from a fresh
 * state. Returns null when the input is not recognisably a farm at all.
 */
export function normalizeState(raw: unknown): GameState | null {
  if (!isObj(raw) || !Array.isArray(raw.plots)) return null;
  if (typeof raw.version === 'number' && raw.version > STATE_VERSION) return null;

  const base = createInitialState(int(raw.rngState, 1, 0, 0xffffffff));
  const rawPlots = raw.plots;

  const upgrades = { ...base.upgrades };
  if (isObj(raw.upgrades)) {
    for (const id of UPGRADE_IDS) upgrades[id] = int(raw.upgrades[id], 0, 0, maxLevel(id));
  }

  const multipliers = { ...base.market.multipliers };
  const rawMarket = isObj(raw.market) ? raw.market : {};
  if (isObj(rawMarket.multipliers)) {
    for (const id of FLOWER_IDS) {
      multipliers[id] = num(rawMarket.multipliers[id], multipliers[id], 0.1, 5);
    }
  }

  const weather = isObj(raw.weather) ? raw.weather : {};
  const stats = isObj(raw.stats) ? raw.stats : {};

  return {
    version: STATE_VERSION,
    farmName:
      sanitizeFarmName(typeof raw.farmName === 'string' ? raw.farmName : '') || base.farmName,
    rngState: int(raw.rngState, base.rngState, 0, 0xffffffff) >>> 0,
    time: num(raw.time, 0, 0),
    day: int(raw.day, 1, 1),
    dayTime: num(raw.dayTime, base.dayTime, 0, 0.999999),
    coins: int(raw.coins, base.coins, 0, 1e12),
    xp: int(raw.xp, 0, 0, 1e12),
    plots: base.plots.map((fallback, id) => parsePlot(rawPlots[id], id, fallback)),
    seeds: flowerRecord(raw.seeds, (v) => (typeof v === 'number' ? int(v, 0, 0, 1e6) : null)),
    flowers: flowerRecord<FlowerStock>(raw.flowers, (v) =>
      isObj(v) ? { n: int(v.n, 0, 0, 1e6), g: int(v.g, 0, 0, 1e6) } : null,
    ),
    upgrades,
    almanac: flowerRecord<AlmanacEntry>(raw.almanac, (v) =>
      isObj(v) ? { harvested: int(v.harvested, 0, 0), golden: int(v.golden, 0, 0) } : null,
    ),
    market: {
      day: int(rawMarket.day, 1, 1),
      multipliers,
      featured: isFlowerId(rawMarket.featured) ? rawMarket.featured : base.market.featured,
    },
    weather: {
      raining: weather.raining === true,
      timeLeft: num(weather.timeLeft, 0, 0, 600),
      nextRainAt: typeof weather.nextRainAt === 'number' ? num(weather.nextRainAt, 0, 0) : null,
      rainbowLeft: num(weather.rainbowLeft, 0, 0, 600),
    },
    orders: Array.isArray(raw.orders)
      ? raw.orders
          .map(parseOrder)
          .filter((o): o is Order => o !== null)
          .slice(0, MAX_ORDERS)
      : base.orders,
    nextOrderId: int(raw.nextOrderId, base.nextOrderId, 1),
    orderTimer: num(raw.orderTimer, base.orderTimer, 0, 600),
    sprinklerTimer: num(raw.sprinklerTimer, 0, 0, 600),
    stats: {
      planted: int(stats.planted, 0, 0),
      harvested: int(stats.harvested, 0, 0),
      coinsEarned: int(stats.coinsEarned, 0, 0),
      ordersCompleted: int(stats.ordersCompleted, 0, 0),
    },
  };
}

/** Validates an action received from the network. Returns null if malformed. */
export function parseGameAction(raw: unknown): GameAction | null {
  if (!isObj(raw) || typeof raw.type !== 'string') return null;
  const plotId = raw.plotId;
  const qty = typeof raw.qty === 'number' && Number.isFinite(raw.qty) ? Math.floor(raw.qty) : NaN;

  switch (raw.type) {
    case 'plant':
      return isValidPlotId(plotId) && isFlowerId(raw.species)
        ? { type: 'plant', plotId, species: raw.species }
        : null;
    case 'water':
    case 'harvest':
    case 'dig':
    case 'unlockPlot':
      return isValidPlotId(plotId) ? { type: raw.type, plotId } : null;
    case 'buySeeds':
      return isFlowerId(raw.species) && qty >= 1 && qty <= 999
        ? { type: 'buySeeds', species: raw.species, qty }
        : null;
    case 'sell':
      return isFlowerId(raw.species) && typeof raw.golden === 'boolean' && qty >= 1 && qty <= 999
        ? { type: 'sell', species: raw.species, golden: raw.golden, qty }
        : null;
    case 'buyUpgrade':
      return isUpgradeId(raw.upgrade) ? { type: 'buyUpgrade', upgrade: raw.upgrade } : null;
    case 'deliverOrder':
    case 'discardOrder':
      return typeof raw.orderId === 'number' && Number.isInteger(raw.orderId)
        ? { type: raw.type, orderId: raw.orderId }
        : null;
    case 'sleep':
      return { type: 'sleep' };
    case 'renameFarm':
      return typeof raw.name === 'string' && raw.name.length <= 64
        ? { type: 'renameFarm', name: raw.name }
        : null;
    default:
      return null;
  }
}
